import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, UserPlus, Phone, MapPin, Calendar, ChevronRight, User, Plus, X, Upload, Pencil, Trash2, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_OPTIONS = ['Cold Call', 'Referral', 'Exhibition/Event', 'Social Media', 'Website', 'WhatsApp', 'Others'];
const DESIGNATIONS = ['Clinic Owner (Orthodontist)', 'Clinic Owner (Non-Orthodontist)', 'Consultant (Orthodontist)', 'Consultant (Non-Orthodontist)', 'Others'];

const Leads = ({ onLogout }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [assigningId, setAssigningId] = useState(null);

  // Assign modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedRep, setSelectedRep] = useState('');

  // Convert inline form
  const [convertingLeadId, setConvertingLeadId] = useState(null);
  const [convertData, setConvertData] = useState({
    clinic_name: '', clinic_address: '', city: '', pincode: '',
    contact_name: '', contact_phone: '', can_influence_decisions: '',
    designation: '', clinic_potential: '',
  });
  const [converting, setConverting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Add new lead form
  const [showAddLead, setShowAddLead] = useState(false);
  const [newLead, setNewLead] = useState({
    contact_name: '', contact_phone: '', source_of_lead: '',
    clinic_name: '', clinic_address: '', city: '', email: '',
  });
  const [savingLead, setSavingLead] = useState(false);

  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const myEmail = localStorage.getItem('userEmail') || '';

  useEffect(() => { fetchLeads(); fetchUsers(); }, []);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(r.data);
    } catch { toast.error('Failed to load leads'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use /leads/users to get ALL users including admin
      const r = await axios.get(`${API}/leads/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(r.data);
    } catch {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/auth/users`, { headers: { Authorization: `Bearer ${token}` } });
        setUsers(r.data);
      } catch {}
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.contact_name || !editForm.contact_name.trim()) { toast.error('Name is required'); return; }
    if (!editForm.contact_phone || !editForm.contact_phone.trim()) { toast.error('Phone is required'); return; }
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (isAdmin) {
        await axios.put(`${API}/leads/${editingLead.id}`, editForm, { headers });
        toast.success('Lead updated ✅');
      } else {
        const changes = {};
        Object.keys(editForm).forEach(key => {
          if (String(editForm[key]||'') !== String(editingLead[key]||''))
            changes[key] = { old_value: editingLead[key]||'', new_value: editForm[key] };
        });
        if (!Object.keys(changes).length) { toast.error('No changes detected'); setSavingEdit(false); return; }
        await axios.post(`${API}/leads/${editingLead.id}/request-change`, { field_changes: changes }, { headers });
        toast.success('Changes submitted for admin approval 🕐');
      }
      setShowEditModal(false);
      fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingEdit(false); }
  };

  const openEdit = (lead) => {
    setEditingLead(lead);
    setEditForm({
      contact_name: lead.contact_name || '',
      contact_phone: lead.contact_phone || '',
      email: lead.email || '',
      city: lead.city || '',
      clinic_name: lead.clinic_name || '',
      clinic_address: lead.clinic_address || '',
      source_of_lead: lead.source_of_lead || '',
      personal_notes: lead.personal_notes || '',
    });
    setShowEditModal(true);
  };


  const handleDeleteLead = async (e, lead) => {
    e.stopPropagation();
    if (!isAdmin) { toast.error('Only admins can delete leads'); return; }
    if (!window.confirm(`⚠️ Delete lead "${lead.contact_name}"?\n\nThis cannot be undone.`)) return;
    const typed = window.prompt(`Type the contact name to confirm:\n\n"${lead.contact_name}"`);
    if (typed !== lead.contact_name) { toast.error('Name did not match — deletion cancelled'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/leads/${lead.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Lead deleted ✅');
      fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const handleAssign = async () => {
    if (!selectedRep) { toast.error('Please select a representative'); return; }
    setAssigningId(selectedLead.id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/leads/${selectedLead.id}/assign`,
        { assigned_to: selectedRep },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lead assigned successfully! ✅');
      setShowAssignModal(false);
      setSelectedLead(null);
      setSelectedRep('');
      fetchLeads();
    } catch { toast.error('Failed to assign lead'); }
    finally { setAssigningId(null); }
  };

  const openConvert = (lead) => {
    setConvertingLeadId(lead.id);
    setConvertData({
      clinic_name: lead.clinic_name || lead.contact_name || '',
      clinic_address: lead.clinic_address || '',
      city: lead.city || '',
      pincode: '',
      contact_name: lead.contact_name || '',
      contact_phone: lead.contact_phone || '',
      can_influence_decisions: lead.can_influence_decisions || '',
      designation: 'Clinic Owner (Orthodontist)',
      clinic_potential: '',
    });
  };

  const handleConvert = async (leadId) => {
    // Validate mandatory fields client-side
    const missing = [];
    if (!convertData.clinic_name) missing.push('Clinic Name');
    if (!convertData.clinic_address) missing.push('Clinic Address');
    if (!convertData.contact_name) missing.push('Contact Name');
    if (!convertData.contact_phone) missing.push('Contact Phone');
    if (!convertData.can_influence_decisions) missing.push('Purchase Influence');
    if (missing.length > 0) { toast.error(`Please fill: ${missing.join(', ')}`); return; }

    setConverting(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/leads/${leadId}/convert`, convertData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lead converted to account! ✅');
      setConvertingLeadId(null);
      fetchLeads();
      // Navigate to the new account
      if (r.data.account_id) {
        setTimeout(() => navigate(`/accounts/${r.data.account_id}`), 1000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to convert lead');
    } finally { setConverting(false); }
  };

  const handleAddLead = async () => {
    if (!newLead.contact_name.trim()) { toast.error('Doctor name is required'); return; }
    if (!newLead.contact_phone.trim()) { toast.error('Mobile number is required'); return; }
    if (!newLead.source_of_lead) { toast.error('Source of lead is required'); return; }
    setSavingLead(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/leads`, newLead, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Lead added! ✅');
      setShowAddLead(false);
      setNewLead({ contact_name: '', contact_phone: '', source_of_lead: '', clinic_name: '', clinic_address: '', city: '', email: '' });
      fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add lead'); }
    finally { setSavingLead(false); }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Parse Excel using SheetJS
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws);
        const token = localStorage.getItem('token');
        let success = 0, failed = 0;
        for (const row of rows) {
          const name = row['Doctor Name'] || row['Name'] || row['contact_name'] || '';
          const phone = String(row['Mobile'] || row['Phone'] || row['contact_phone'] || '');
          const source = row['Source'] || row['source_of_lead'] || 'Others';
          if (!name || !phone) { failed++; continue; }
          try {
            await axios.post(`${API}/leads`, {
              contact_name: name, contact_phone: phone, source_of_lead: source,
              clinic_name: row['Clinic Name'] || '', clinic_address: row['Address'] || '',
              city: row['City'] || '',
            }, { headers: { Authorization: `Bearer ${token}` } });
            success++;
          } catch { failed++; }
        }
        toast.success(`Imported ${success} leads${failed ? `, ${failed} failed` : ''}!`);
        fetchLeads();
      } catch { toast.error('Failed to parse Excel file'); }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const filtered = leads.filter(l => {
    const q = searchQuery.toLowerCase();
    return !q || l.contact_name?.toLowerCase().includes(q) ||
      l.contact_phone?.includes(q) || l.city?.toLowerCase().includes(q) ||
      l.clinic_name?.toLowerCase().includes(q) ||
      l.assigned_to?.toLowerCase().includes(q) ||
      l.source_of_lead?.toLowerCase().includes(q);
  });

  // Filter: hide converted, show own + assigned
  const activeLeads = filtered.filter(l => !l.converted_to_account);
  const myLeads = isAdmin ? activeLeads : activeLeads.filter(l =>
    l.created_by === myEmail || l.assigned_to === myEmail
  );

  const purposeColors = {
    'Product Promotion': 'bg-teal-100 text-teal-700',
    'Complaint Resolution': 'bg-red-100 text-red-700',
    'Webinar/Workshop Invite': 'bg-purple-100 text-purple-700',
    'Case Follow-Up': 'bg-blue-100 text-blue-700',
    'Post Webinar/Workshop Follow-Up': 'bg-amber-100 text-amber-700',
    'Others': 'bg-slate-100 text-slate-600',
  };

  const formatDate = d => {
    if (!d) return '—';
    if (d === 'assign-to-rep') return '👤 Assign to Rep';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>📋 Leads</h1>
            <p className="text-sm text-slate-500 mt-1">{myLeads.length} active leads</p>
          </div>
          <div className="flex gap-2">
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleExcelUpload} className="hidden" />
            <button onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2.5 border-2 border-teal-200 text-teal-700 rounded-xl text-sm font-semibold hover:bg-teal-50 transition-all">
              <Upload className="h-4 w-4" /> Import Excel
            </button>
            <button onClick={() => setShowAddLead(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl text-sm font-semibold hover:from-teal-800 hover:to-emerald-600 transition-all">
              <Plus className="h-4 w-4" /> Add New Lead
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by name, phone, clinic, city, source or rep..."
            className="pl-10 h-12 rounded-xl" />
        </div>

        {/* Add New Lead Form */}
        {showAddLead && (
          <div className="mb-6 bg-white rounded-2xl border-2 border-teal-200 shadow-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800 text-lg">➕ Add New Lead</h3>
              <button onClick={() => setShowAddLead(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Doctor Name *</Label>
                <Input value={newLead.contact_name} onChange={e => setNewLead(p => ({...p, contact_name: e.target.value}))}
                  placeholder="Dr. Full Name" className="rounded-xl h-10" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Mobile Number *</Label>
                <Input value={newLead.contact_phone} onChange={e => setNewLead(p => ({...p, contact_phone: e.target.value}))}
                  placeholder="10-digit mobile" className="rounded-xl h-10" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Source of Lead *</Label>
                <select value={newLead.source_of_lead} onChange={e => setNewLead(p => ({...p, source_of_lead: e.target.value}))}
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-teal-500">
                  <option value="">Select source...</option>
                  {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Email (Optional)</Label>
                <Input value={newLead.email} onChange={e => setNewLead(p => ({...p, email: e.target.value}))}
                  placeholder="email@example.com" type="email" className="rounded-xl h-10" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Clinic Name (Optional)</Label>
                <Input value={newLead.clinic_name} onChange={e => setNewLead(p => ({...p, clinic_name: e.target.value}))}
                  placeholder="Clinic / Hospital name" className="rounded-xl h-10" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">City (Optional)</Label>
                <Input value={newLead.city} onChange={e => setNewLead(p => ({...p, city: e.target.value}))}
                  placeholder="City" className="rounded-xl h-10" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Address (Optional)</Label>
                <Input value={newLead.clinic_address} onChange={e => setNewLead(p => ({...p, clinic_address: e.target.value}))}
                  placeholder="Clinic address" className="rounded-xl h-10" />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => setShowAddLead(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleAddLead} disabled={savingLead}
                className="flex-1 bg-gradient-to-r from-teal-700 to-emerald-500 text-white">
                {savingLead ? 'Saving...' : '✅ Save Lead'}
              </Button>
            </div>
          </div>
        )}

        {/* Leads List */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full mx-auto" />
            <p className="text-slate-500 mt-4">Loading leads...</p>
          </div>
        ) : myLeads.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <div className="p-4 bg-teal-50 rounded-full inline-block mb-4">
              <UserPlus className="h-10 w-10 text-teal-600" />
            </div>
            <p className="text-slate-600 text-lg font-medium mb-2">No active leads</p>
            <p className="text-slate-400 text-sm mb-4">Add a lead manually or import from Excel</p>
            <button onClick={() => setShowAddLead(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl text-sm font-semibold">
              + Add First Lead
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {myLeads.map(lead => (
              <div key={lead.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    {/* Left */}
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-600 to-emerald-400 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">{lead.contact_name?.charAt(0) || '?'}</span>
                      </div>
                      <div>
                        <p className="font-bold text-slate-800">Dr. {lead.contact_name}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          {lead.contact_phone && (
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <Phone className="h-3 w-3" /> {lead.contact_phone}
                            </span>
                          )}
                          {lead.city && (
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <MapPin className="h-3 w-3" /> {lead.city}
                            </span>
                          )}
                          {lead.clinic_name && (
                            <span className="text-xs text-slate-500">🏥 {lead.clinic_name}</span>
                          )}
                        </div>
                        {/* Source badge */}
                        {lead.source_of_lead && (
                          <span className="mt-1 inline-block text-xs px-2 py-0.5 bg-violet-50 text-violet-700 rounded-lg border border-violet-100 font-medium">
                            📌 {lead.source_of_lead}
                          </span>
                        )}
                        {/* Purposes */}
                        {lead.telephonic_purposes?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {lead.telephonic_purposes.map(p => (
                              <span key={p} className={`text-xs px-2 py-0.5 rounded-lg font-medium ${purposeColors[p] || 'bg-slate-100 text-slate-600'}`}>{p}</span>
                            ))}
                          </div>
                        )}
                        {lead.follow_up_date && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                            <Calendar className="h-3 w-3" /> Follow-up: {formatDate(lead.follow_up_date)}
                          </div>
                        )}
                        {lead.personal_notes && (
                          <p className="text-xs text-slate-400 mt-1 italic line-clamp-1">"{lead.personal_notes}"</p>
                        )}
                      </div>
                    </div>
                    {/* Right — status */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {lead.assigned_to ? (
                        <span className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2 py-1 rounded-lg flex items-center gap-1">
                          <User className="h-3 w-3" />{lead.assigned_to.split('@')[0]}
                        </span>
                      ) : (
                        <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded-lg">Unassigned</span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                    <button type="button"
                      onClick={() => { setSelectedLead(lead); setShowAssignModal(true); }}
                      className="flex-1 py-2 rounded-xl border border-teal-200 text-teal-700 text-xs font-semibold hover:bg-teal-50 flex items-center justify-center gap-1">
                      <UserPlus className="h-3.5 w-3.5" />
                      {lead.assigned_to ? 'Reassign' : 'Assign to Rep'}
                    </button>
                    <button type="button" onClick={() => openEdit(lead)}
                      className="py-2 px-3 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold hover:bg-teal-50 hover:text-teal-700 hover:border-teal-200 flex items-center gap-1">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    {isAdmin && (
                      <button type="button" onClick={e => handleDeleteLead(e, lead)}
                        className="py-2 px-3 rounded-xl border border-red-100 text-red-400 text-xs font-semibold hover:bg-red-50 hover:text-red-600 flex items-center gap-1">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button type="button"
                      onClick={() => convertingLeadId === lead.id ? setConvertingLeadId(null) : openConvert(lead)}
                      className="flex-1 py-2 rounded-xl bg-gradient-to-r from-teal-700 to-emerald-500 text-white text-xs font-semibold hover:from-teal-800 hover:to-emerald-600 flex items-center justify-center gap-1">
                      <ChevronRight className="h-3.5 w-3.5" />
                      Convert to Account
                    </button>
                  </div>
                </div>

                {/* Inline Convert Form */}
                {convertingLeadId === lead.id && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-3">
                      <p className="text-sm font-bold text-emerald-800">Complete details to convert to account</p>
                      <p className="text-xs text-emerald-600">All fields marked * are required for conversion</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-semibold text-slate-600 mb-1 block">Clinic Name *</Label>
                          <Input value={convertData.clinic_name} onChange={e => setConvertData(p => ({...p, clinic_name: e.target.value}))}
                            placeholder="Clinic / Hospital name" className="rounded-xl h-9 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-slate-600 mb-1 block">City</Label>
                          <Input value={convertData.city} onChange={e => setConvertData(p => ({...p, city: e.target.value}))}
                            placeholder="City" className="rounded-xl h-9 text-sm" />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs font-semibold text-slate-600 mb-1 block">Clinic Address *</Label>
                          <Input value={convertData.clinic_address} onChange={e => setConvertData(p => ({...p, clinic_address: e.target.value}))}
                            placeholder="Full address" className="rounded-xl h-9 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-slate-600 mb-1 block">Contact Name *</Label>
                          <Input value={convertData.contact_name} onChange={e => setConvertData(p => ({...p, contact_name: e.target.value}))}
                            placeholder="Doctor name" className="rounded-xl h-9 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-slate-600 mb-1 block">Contact Phone *</Label>
                          <Input value={convertData.contact_phone} onChange={e => setConvertData(p => ({...p, contact_phone: e.target.value}))}
                            placeholder="Mobile number" className="rounded-xl h-9 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-slate-600 mb-1 block">Purchase Influence *</Label>
                          <div className="flex gap-2">
                            {['High', 'Medium', 'Low'].map(opt => (
                              <button key={opt} type="button" onClick={() => setConvertData(p => ({...p, can_influence_decisions: opt}))}
                                className={`flex-1 py-2 rounded-xl border-2 text-xs font-bold transition-all ${
                                  convertData.can_influence_decisions === opt
                                    ? opt === 'High' ? 'bg-emerald-600 text-white border-emerald-600'
                                    : opt === 'Medium' ? 'bg-amber-500 text-white border-amber-500'
                                    : 'bg-red-500 text-white border-red-500'
                                    : 'bg-white text-slate-600 border-slate-200'
                                }`}>{opt}</button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-slate-600 mb-1 block">Designation</Label>
                          <select value={convertData.designation} onChange={e => setConvertData(p => ({...p, designation: e.target.value}))}
                            className="w-full h-9 px-2 rounded-xl border border-slate-200 bg-white text-xs focus:outline-none focus:border-teal-500">
                            {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-slate-600 mb-1 block">Clinic Potential</Label>
                          <div className="flex gap-2">
                            {['High', 'Medium', 'Low'].map(opt => (
                              <button key={opt} type="button" onClick={() => setConvertData(p => ({...p, clinic_potential: opt}))}
                                className={`flex-1 py-1.5 rounded-xl border-2 text-xs font-bold transition-all ${
                                  convertData.clinic_potential === opt
                                    ? 'bg-teal-700 text-white border-teal-700'
                                    : 'bg-white text-slate-600 border-slate-200'
                                }`}>{opt}</button>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button type="button" onClick={() => setConvertingLeadId(null)}
                          className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50">
                          Cancel
                        </button>
                        <button type="button" onClick={() => handleConvert(lead.id)} disabled={converting}
                          className="flex-1 py-2.5 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl text-sm font-semibold">
                          {converting ? 'Converting...' : '✅ Convert to Account'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Assign Modal */}
        {showAssignModal && selectedLead && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
              <h3 className="font-bold text-slate-800 text-lg mb-1">Assign Lead to Rep</h3>
              <p className="text-sm text-slate-500 mb-4">Dr. {selectedLead.contact_name} · {selectedLead.contact_phone}</p>
              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                {users.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No users found</p>}
                {users.map(u => (
                  <button key={u.id} type="button" onClick={() => setSelectedRep(u.email)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl border-2 text-sm transition-all ${
                      selectedRep === u.email ? 'border-teal-500 bg-teal-50 font-semibold text-teal-800' : 'border-slate-200 hover:border-teal-300'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{u.name} {u.is_admin ? '👑' : ''}</p>
                        <p className="text-xs text-slate-400">{u.email}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setShowAssignModal(false); setSelectedRep(''); }} className="flex-1">Cancel</Button>
                <Button onClick={handleAssign} disabled={!!assigningId}
                  className="flex-1 bg-gradient-to-r from-teal-700 to-emerald-500 text-white">
                  {assigningId ? 'Assigning...' : 'Assign'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* ── Edit Lead Modal ── */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle>{isAdmin ? 'Edit Lead' : 'Edit Lead — Submit for Approval'}</DialogTitle>
          </DialogHeader>
          {!isAdmin && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              ⏳ As a rep, your changes will be sent to admin for approval before taking effect.
            </p>
          )}
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Doctor Name *</Label>
                <Input value={editForm.contact_name || ''} onChange={e => setEditForm(p => ({...p, contact_name: e.target.value}))} className="rounded-xl h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Mobile *</Label>
                <Input value={editForm.contact_phone || ''} onChange={e => setEditForm(p => ({...p, contact_phone: e.target.value}))} className="rounded-xl h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Clinic Name</Label>
                <Input value={editForm.clinic_name || ''} onChange={e => setEditForm(p => ({...p, clinic_name: e.target.value}))} className="rounded-xl h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">City</Label>
                <Input value={editForm.city || ''} onChange={e => setEditForm(p => ({...p, city: e.target.value}))} className="rounded-xl h-9 text-sm" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Address</Label>
                <Input value={editForm.clinic_address || ''} onChange={e => setEditForm(p => ({...p, clinic_address: e.target.value}))} className="rounded-xl h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Email</Label>
                <Input value={editForm.email || ''} type="email" onChange={e => setEditForm(p => ({...p, email: e.target.value}))} className="rounded-xl h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Source</Label>
                <select value={editForm.source_of_lead || ''} onChange={e => setEditForm(p => ({...p, source_of_lead: e.target.value}))}
                  className="w-full h-9 px-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-teal-500">
                  <option value="">Select...</option>
                  {['Cold Call','Referral','Exhibition/Event','Social Media','Website','WhatsApp','Others'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold text-slate-600 mb-1 block">Notes</Label>
                <Input value={editForm.personal_notes || ''} onChange={e => setEditForm(p => ({...p, personal_notes: e.target.value}))} className="rounded-xl h-9 text-sm" />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSaveEdit} disabled={savingEdit}
                className="flex-1 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl">
                <Save className="h-4 w-4 mr-1" />
                {savingEdit ? 'Saving...' : isAdmin ? 'Save Changes' : 'Submit for Approval'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </Layout>
  );
};

export default Leads;
